// frontend/src/hooks/useUsers.ts — the users read surface (list · detail · pool
// search). Project-scoped server state (FRONTEND-PATTERNS §State): keys are
// `['users', projectId, …]` so a mutation's invalidate(['users', projectId])
// refreshes list + detail + search together (§Mutations + invalidation).
//
// 401 is never touched here — the centralized apiClient owns it (ADR-003); these
// hooks let the error bubble so the page's 5-state contract / the form shell can
// render it.

import { useMemo } from 'react';
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { DepartureImpact, ProjectUserDetail, ProjectUsersListResponse, UserSearchResponse } from '@ems-app/schemas/users/responses';
import type { UserLookupRow, UserLookupRule } from '@ems-app/schemas/users/lookup';
import { apiClient } from '../lib/apiClient.js';
import { useLookup, type LookupSource } from './useLookup.js';
import type { FacedRow, ServerSource } from './useOptions.js';
import { useDebouncedValue } from './useDebouncedValue.js';

/** The people as a picker's source (ADR-080 §3 — the server kind; the face is
 *  `usePersonFace()`). A FILTER's people (`ticket_authors` · `audit_actors`)
 *  pass the viewer (`me`): their own row answers as the token `me`, which the
 *  link and the wire carry and the server resolves (ADR-080 §6). */
export function personSource(
  projectId: string,
  rule: UserLookupRule,
  face: (row: UserLookupRow) => FacedRow,
  opts: { level?: string; scope?: string; me?: { id: string; label: string } } = {},
): ServerSource<'users'> {
  return { kind: 'server', projectId, lookup: { entity: 'users', params: { rule, level: opts.level, scope: opts.scope } }, face, me: opts.me };
}

/** The NAMES of a few people by id — the person lookup's rows of given ids (ADR-051's resolver made bounded, ADR-078 §6):
 *  a surface that shows who answers asks "name these codes" (a handful), never a directory. `rule` names the read whose
 *  gate the names ride — `members` for any reader of the place; `accountable` with its level + scope for the chain
 *  editor, whose rows then carry `covered` — and the rows share the picker's cache on the same source, so a person the
 *  field met by typing is named here too. Asks nothing for an empty list; `unavailable` holds the codes the server
 *  answered absent (outside the project, or gone). */
export function useNamedPeople(
  projectId: string,
  ids: readonly string[],
  opts: { rule?: UserLookupRule; level?: string; scope?: string; enabled?: boolean } = {},
): { byId: ReadonlyMap<string, UserLookupRow>; naming: boolean; unavailable: ReadonlySet<string> } {
  const { rule = 'members', level, scope, enabled = true } = opts;
  const source = useMemo<LookupSource<'users'>>(() => ({ entity: 'users', params: { rule, level, scope } }), [rule, level, scope]);
  const key = ids.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the ids stand in by their joined key (a caller's fresh array each render)
  const stable = useMemo(() => [...new Set(ids)], [key]);
  const look = useLookup(projectId, source, stable, { open: false, enabled: enabled && stable.length > 0 });
  return { byId: look.known, naming: look.naming, unavailable: look.unavailable };
}

export const usersKeys = {
  /** List key — also the invalidation PREFIX (covers detail + search). */
  all: (projectId: string) => ['users', projectId] as const,
  detail: (projectId: string, userId: string) => ['users', projectId, userId] as const,
  search: (projectId: string, q: string) => ['users', projectId, 'search', q] as const,
  impact: (projectId: string, userId: string) => ['users', projectId, userId, 'impact'] as const,
};

// ── the departure's impact (ADR-081 §4–§5) ──────────────────────────────────
/** GET …/users/:id/impact — what removing or disabling this person touches: the chains they hold here (position ·
 *  who remains · alone), the personal rows the leave ends, the live tokens; a sysadmin also sees the chains held
 *  elsewhere. Asked only while the dialog that shows it is open. */
export function useDepartureImpact(projectId: string, userId: string, enabled: boolean): UseQueryResult<DepartureImpact, Error> {
  return useQuery({
    queryKey: usersKeys.impact(projectId, userId),
    queryFn: async () => (await apiClient.get<DepartureImpact>(`/projects/${projectId}/users/${userId}/impact`)).data,
    enabled: enabled && projectId.length > 0 && userId.length > 0,
  });
}

// ── list ────────────────────────────────────────────────────────────────────
async function fetchUsers(projectId: string): Promise<ProjectUsersListResponse> {
  const r = await apiClient.get<ProjectUsersListResponse>(`/projects/${projectId}/users`);
  return r.data;
}

// The full envelope: `items` + `has_telegram_bot` (the project-level fact the
// Telegram column hides on — said once by the server, consumed once here).
export function useUsers(projectId: string): UseQueryResult<ProjectUsersListResponse, Error> {
  return useQuery({
    queryKey: usersKeys.all(projectId),
    queryFn: () => fetchUsers(projectId),
    enabled: projectId.length > 0,
  });
}

// ── detail ────────────────────────────────────────────────────────────────────
/** The person's detail read. `quiet` — a form's Load the latest: a person gone from the project meanwhile answers the
 *  form, never the not-found page (ADR-084 §6). */
export async function fetchUser(projectId: string, userId: string, quiet = false): Promise<ProjectUserDetail> {
  const r = await apiClient.get<ProjectUserDetail>(`/projects/${projectId}/users/${userId}`, quiet ? { quietNotFound: true } : undefined);
  return r.data;
}

export function useUser(projectId: string, userId: string): UseQueryResult<ProjectUserDetail, Error> {
  return useQuery({
    queryKey: usersKeys.detail(projectId, userId),
    queryFn: () => fetchUser(projectId, userId),
    enabled: projectId.length > 0 && userId.length > 0,
  });
}

// ── pool search ───────────────────────────────────────────────────────────────
async function fetchUserSearch(projectId: string, q: string): Promise<UserSearchResponse> {
  const r = await apiClient.get<UserSearchResponse>(`/projects/${projectId}/users/search`, { params: { q } });
  return r.data;
}

/** Search-first global pool (q required, capped + `has_more`). Keeps the prior
 *  page of results on screen while the next query is in flight (no flicker).
 *  `isSearching` is true when the user has typed something but results aren't
 *  settled for it yet (debounce pending OR fetch in flight) — so the picker can
 *  show a quiet hint instead of a premature "no results". */
export function useUserSearch(
  projectId: string,
  query: string,
  debounceMs = 300,
): UseQueryResult<UserSearchResponse, Error> & { isSearching: boolean } {
  const trimmed = query.trim();
  const q = useDebouncedValue(trimmed, debounceMs);
  const result = useQuery({
    queryKey: usersKeys.search(projectId, q),
    queryFn: () => fetchUserSearch(projectId, q),
    enabled: projectId.length > 0 && q.length > 0,
    placeholderData: keepPreviousData,
  });
  const isSearching = trimmed.length > 0 && (q !== trimmed || result.isFetching);
  return Object.assign(result, { isSearching });
}
